import { useId, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { ChargeView, TenantAddonView } from '@/api/generated';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useActionError } from '@/components/useActionError';
import { useToast } from '@/components/useToast';
import { visuallyHidden } from '@/components/visuallyHidden';
import { useLocale } from '@/i18n/useLocale';
import { useCanWrite } from '@/auth/authHooks';
import { Icon, resolveTokens } from '@/theme';
import { StatusChip } from '@/components/status/StatusChip';
import { addonStatusKey, billingLabel, priceLabel, termLabel } from './addonLabels';
import { ChargeHistory } from './ChargeHistory';

export interface AddonTableProps {
  addons: TenantAddonView[];
  busy: boolean;
  onAccept: (addon: TenantAddonView) => void;
  onDecline: (id: number) => Promise<void>;
  onCancel: (id: number) => Promise<void>;
  onUpdateCard: (addon: TenantAddonView) => void;
  onRetry: (addon: TenantAddonView, charge: ChargeView) => void;
  /** The charge whose confirmed retry is running; its Retry is disabled. */
  retryingChargeId?: number | null;
}

export function AddonTable({
  addons,
  busy,
  onAccept,
  onDecline,
  onCancel,
  onUpdateCard,
  onRetry,
  retryingChargeId = null,
}: AddonTableProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const showToast = useToast();
  const describe = useActionError();
  const [expandedId, setExpandedId] = useState<number | undefined>(undefined);
  const [declining, setDeclining] = useState<TenantAddonView | null>(null);
  const [cancelling, setCancelling] = useState<TenantAddonView | null>(null);

  async function handleDecline() {
    if (!declining) return;
    await onDecline(declining.id);
    showToast(t('addons.toast.declined'));
  }

  async function handleCancel() {
    if (!cancelling) return;
    await onCancel(cancelling.id);
    showToast(t('addons.toast.cancelled'));
  }

  if (addons.length === 0) {
    return (
      <Paper sx={{ p: 2.5 }}>
        <Typography>{t('addons.empty')}</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Table size="small" aria-label={t('pages.addons.title')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('addons.col.name')}</TableCell>
            <TableCell align="right">{t('addons.col.price')}</TableCell>
            <TableCell>{t('addons.col.term')}</TableCell>
            <TableCell>{t('addons.col.billing')}</TableCell>
            <TableCell>{t('addons.col.status')}</TableCell>
            <TableCell align="right">
              <Box component="span" sx={visuallyHidden}>
                {t('common.actions')}
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {addons.map((addon) => {
            const statusKey = addonStatusKey(addon);
            const selected = addon.id === expandedId;
            const live = addon.status === 'ACTIVE' || addon.status === 'PAST_DUE';
            return (
              <AddonRows
                key={addon.id}
                addon={addon}
                statusKey={statusKey}
                selected={selected}
                live={live}
                busy={busy}
                tokensBrandSoft={tokens.brandSoft}
                tokensText2={tokens.text2}
                price={priceLabel(addon, locale, addon.currency)}
                term={termLabel(addon, locale)}
                billing={billingLabel(addon)}
                onToggle={() => {
                  setExpandedId(selected ? undefined : addon.id);
                }}
                onAccept={() => onAccept(addon)}
                onDecline={() => setDeclining(addon)}
                onCancel={() => setCancelling(addon)}
                onUpdateCard={() => onUpdateCard(addon)}
                onRetry={onRetry}
                retryingChargeId={retryingChargeId}
              />
            );
          })}
        </TableBody>
      </Table>
      <ConfirmDialog
        open={declining !== null}
        title={t('addons.declineTitle', { name: declining ? declining.name : '' })}
        consequence={t('addons.declineBody')}
        confirmLabel={t('addons.declineConfirm')}
        onConfirm={handleDecline}
        onCancel={() => setDeclining(null)}
        errorMessage={(err) => describe(err).message}
      />
      <ConfirmDialog
        open={cancelling !== null}
        title={t('addons.cancelTitle', { name: cancelling ? cancelling.name : '' })}
        consequence={t('addons.cancelBody')}
        confirmLabel={t('addons.cancelConfirm')}
        cancelLabel={t('addons.keep', { name: cancelling ? cancelling.name : '' })}
        destructive
        onConfirm={handleCancel}
        onCancel={() => setCancelling(null)}
        errorMessage={(err) => describe(err).message}
      />
    </Paper>
  );
}

function AddonRows({
  addon,
  statusKey,
  selected,
  live,
  busy,
  tokensBrandSoft,
  tokensText2,
  price,
  term,
  billing,
  onToggle,
  onAccept,
  onDecline,
  onCancel,
  onUpdateCard,
  onRetry,
  retryingChargeId,
}: {
  addon: TenantAddonView;
  statusKey: string | undefined;
  selected: boolean;
  live: boolean;
  busy: boolean;
  tokensBrandSoft: string;
  tokensText2: string;
  price: string;
  term: string;
  billing: string;
  onToggle: () => void;
  onAccept: () => void;
  onDecline: () => void;
  onCancel: () => void;
  onUpdateCard: () => void;
  onRetry: (addon: TenantAddonView, charge: ChargeView) => void;
  retryingChargeId: number | null;
}) {
  const { t } = useTranslation();
  const canWrite = useCanWrite();
  const regionId = useId();
  const showUpdateCard = canWrite && live && addon.billingMode === 'CARD';
  const showCancel = canWrite && live && !addon.cancelAtTermEnd;
  const name = addon.name;

  return (
    <>
      <TableRow
        hover
        selected={selected}
        // A mouse convenience: the disclosure button is the control.
        onClick={onToggle}
        sx={{
          cursor: 'pointer',
          bgcolor: selected ? tokensBrandSoft : undefined,
        }}
      >
        <TableCell component="th" scope="row">
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <IconButton
              size="small"
              aria-expanded={selected}
              aria-controls={regionId}
              aria-label={t('common.showDetailsFor', { name })}
              onClick={(event) => {
                event.stopPropagation();
                onToggle();
              }}
            >
              <Icon name={selected ? 'expand_less' : 'expand_more'} />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography>{name}</Typography>
              {addon.description ? (
                <Typography variant="caption" sx={{ display: 'block', color: tokensText2 }}>
                  {addon.description}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {price}
        </TableCell>
        <TableCell>{term}</TableCell>
        <TableCell>{billing}</TableCell>
        <TableCell>
          <StatusChip domain="addon" status={statusKey} />
        </TableCell>
        <TableCell align="right" onClick={(event) => event.stopPropagation()}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {canWrite && addon.status === 'OFFERED' ? (
              <>
                <Button disabled={busy} onClick={onAccept}>
                  {t('addons.accept')}
                </Button>
                <Button color="error" disabled={busy} onClick={onDecline}>
                  {t('addons.decline')}
                </Button>
              </>
            ) : null}
            {showUpdateCard ? (
              <Button disabled={busy} onClick={onUpdateCard}>
                {t('addons.updateCard')}
              </Button>
            ) : null}
            {showCancel ? (
              <Button color="error" disabled={busy} onClick={onCancel}>
                {t('addons.cancelAtTermEnd')}
              </Button>
            ) : null}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: selected ? undefined : 0 }}>
          <Collapse in={selected} unmountOnExit id={regionId}>
            <Box sx={{ py: 2 }}>
              {addon.notes ? <Typography sx={{ mb: 2 }}>{addon.notes}</Typography> : null}
              <ChargeHistory
                addons={[addon]}
                busy={busy}
                onRetry={onRetry}
                retryingChargeId={retryingChargeId}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
